import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { ShieldCheck, Trash2, Plus, AlertCircle, CheckCircle2 } from "lucide-react";
import FileUpload from "@/components/FileUpload";

export default function RecognitionsManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    body_name: "", short_code: "", description: "",
    certificate_url: "", logo_url: "", link: "",
    issued_year: "", priority: 5,
  });
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const refresh = () => api.get("/recognitions").then((r) => setItems(r.data));
  useEffect(() => { refresh(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.certificate_url) { setErr("Please upload a certificate image."); return; }
    setErr(""); setOk(""); setLoading(true);
    try {
      const payload = { ...form, issued_year: form.issued_year ? Number(form.issued_year) : null, priority: Number(form.priority) || 0 };
      await api.post("/recognitions", payload);
      setForm({ body_name: "", short_code: "", description: "", certificate_url: "", logo_url: "", link: "", issued_year: "", priority: 5 });
      setOk("Recognition added. It now appears on the public Recognitions page.");
      refresh();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this recognition?")) return;
    try { await api.delete(`/recognitions/${id}`); refresh(); } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="recognitions-manager">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-nkf-red" />
        <h2 className="font-display text-xl text-nkf-blue">Federation Recognitions</h2>
      </div>
      <p className="text-sm text-slate-600 mt-1">
        Upload certificates of recognition from international and national bodies (ITKF, UKF, IAKS, TAFISA, etc.). They appear on <span className="font-semibold">/recognitions</span>.
      </p>

      <form onSubmit={add} className="mt-5 grid grid-cols-1 md:grid-cols-12 gap-3">
        <Input wide="md:col-span-5" label="Recognising Body *" value={form.body_name} set={(v) => set("body_name", v)} required testid="rec-body" />
        <Input wide="md:col-span-2" label="Short Code" value={form.short_code} set={(v) => set("short_code", v)} placeholder="ITKF" testid="rec-code" />
        <Input wide="md:col-span-3" label="Issued Year" value={form.issued_year} set={(v) => set("issued_year", v)} placeholder="2024" testid="rec-year" />
        <Input wide="md:col-span-2" label="Priority" value={form.priority} set={(v) => set("priority", v)} placeholder="5" testid="rec-priority" />

        <div className="md:col-span-6">
          <FileUpload value={form.certificate_url} onChange={(v) => set("certificate_url", v)} label="Certificate Image *" testid="rec-cert-upload" />
        </div>
        <div className="md:col-span-6">
          <FileUpload value={form.logo_url} onChange={(v) => set("logo_url", v)} label="Body Logo (optional)" testid="rec-logo-upload" />
        </div>

        <Input wide="md:col-span-8" label="Description" value={form.description} set={(v) => set("description", v)} placeholder="Short note about the recognition" testid="rec-desc" />
        <Input wide="md:col-span-4" label="External Link" value={form.link} set={(v) => set("link", v)} placeholder="https://www.itkf.org" testid="rec-link" />
        <div className="md:col-span-12 flex justify-end">
          <button disabled={loading} data-testid="rec-add" className="inline-flex items-center gap-1 bg-nkf-blue text-white text-sm font-bold uppercase tracking-widest px-5 py-2.5 rounded hover:bg-nkf-navy transition disabled:opacity-60">
            <Plus className="h-4 w-4" /> {loading ? "Saving…" : "Add Recognition"}
          </button>
        </div>
      </form>

      {err && <div className="mt-3 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 p-2.5 rounded text-sm"><AlertCircle className="h-4 w-4 mt-0.5" /> {err}</div>}
      {ok && <div className="mt-3 flex items-start gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 p-2.5 rounded text-sm"><CheckCircle2 className="h-4 w-4 mt-0.5" /> {ok}</div>}

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((r) => (
          <div key={r.id} className="border border-slate-200 rounded-md p-3 flex gap-3">
            <img src={r.certificate_url} alt="" className="h-16 w-16 object-cover rounded bg-slate-100" />
            <div className="flex-1 min-w-0">
              <div className="font-display text-sm text-nkf-blue truncate">{r.body_name}</div>
              <div className="text-[10.5px] uppercase tracking-widest text-nkf-red">{r.short_code || "—"} · Priority {r.priority}</div>
              <button onClick={() => remove(r.id)} data-testid={`rec-del-${r.id}`} className="mt-1.5 text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1">
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </button>
            </div>
          </div>
        ))}
        {!items.length && <div className="md:col-span-3 text-center text-slate-400 text-sm py-6 border border-dashed rounded-md">No recognitions uploaded yet.</div>}
      </div>
    </section>
  );
}

function Input({ wide, label, value, set, required, placeholder, testid }) {
  return (
    <label className={`block ${wide || ""}`} data-testid={testid}>
      <span className="block text-[11px] uppercase tracking-widest text-slate-500 mb-1 font-semibold">{label}</span>
      <input required={required} placeholder={placeholder} value={value} onChange={(e) => set(e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-nkf-blue/30 focus:border-nkf-blue" />
    </label>
  );
}
